import { Component, computed, inject, OnInit, signal, ViewChild } from '@angular/core';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonMenuButton, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  WorkDocument,
  WorkDocumentsV2Service,
  WorkNews,
  WorkNewsReadConfirmation,
  WorkNewsV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import { lastValueFrom } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { InfoblockComponent } from '@work-frontend-monorepo/work-ui/data-display';
import { FormatDatePipe, PrependServerToFileSrcPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { ExceptionHandlingService, LoggerService } from '@work-frontend-monorepo/work-ui/services';
import { LightboxComponent } from '@work-frontend-monorepo/work-ui/rich-content';
import { ButtonComponent, IconComponent } from '@work-frontend-monorepo/work-ui/layout';
import { AlertDialogService } from '@work-frontend-monorepo/work-ui/modals';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';
import { environment } from '@work-frontend-monorepo/work-environments';

@Component({
  selector: 'sfg-app-mitteilungen-details-page',
  templateUrl: './mitteilungen-details-page.component.html',
  styleUrls: ['./mitteilungen-details-page.component.scss'],
  standalone: true,
  imports: [
    FormatDatePipe,
    InfoblockComponent,
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonTitle,
    IonToolbar,
    PrependServerToFileSrcPipe,
    ReactiveFormsModule,
    TranslatePipe,
    LightboxComponent,
    IconComponent,
    ButtonComponent,
  ],
})
export class MitteilungenDetailsPageComponent implements OnInit {
  @ViewChild('lightbox') lightbox!: LightboxComponent;

  public readonly news = signal<WorkNews | undefined>(undefined);

  protected readonly photos = computed<WorkDocument[]>(() => {
    const documents = this.news()?.documents ?? [];
    return documents.filter((document) => this.isImage(document));
  });

  protected readonly attachments = computed<Array<WorkDocument & { downloadUrl: string }>>(() => {
    const documents = this.news()?.documents ?? [];
    const baseUrl = environment.apiConfiguration.baseUrl;
    const downloadPath = WorkDocumentsV2Service.DocumentsV2ControllerDownloadDocumentsPath;
    return documents
      .filter((document) => !this.isImage(document))
      .map((document) => ({ ...document, downloadUrl: `${baseUrl}${downloadPath}?ids=${document.id}` }));
  });

  /** Lesestand der angemeldeten Person zu dieser Mitteilung. */
  protected readonly readConfirmation = signal<WorkNewsReadConfirmation | undefined>(undefined);
  protected confirmLoadingState: LoadingAndFeedbackState = 'none';

  /**
   * Sichtbar, solange die Mitteilung eine aktive Lesebestaetigung verlangt und diese noch aussteht.
   *
   * Das blosse Oeffnen (weicher Lesestand) reicht dafuer bewusst nicht.
   */
  protected readonly showConfirmationRequest = computed(
    () => !!this.news()?.requestReadConfirmation && !this.readConfirmation()?.confirmedAt,
  );

  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly workNewsV2Service = inject(WorkNewsV2Service);
  private readonly activatedRoute = inject(ActivatedRoute);

  async ngOnInit() {
    const newsId = this.activatedRoute.snapshot.params['newsId'];
    if (newsId) {
      await this.loadNewsDetails(newsId);
    }
  }

  protected openLightbox(photo: WorkDocument): void {
    this.lightbox.open(photo);
  }

  private isImage(document: WorkDocument): boolean {
    return (document.mimetype ?? '').startsWith('image/');
  }

  private async loadNewsDetails(newsId: number) {
    try {
      const newsResponse = await lastValueFrom(
        this.workNewsV2Service.newsV2ControllerFindOneById({
          id: newsId,
        }),
      );
      this.news.set(newsResponse);
      await this.markAsRead(newsId);
    } catch (loadUserDetailsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadUserDetailsException', loadUserDetailsException);
    }
  }

  /**
   * Weicher Lesestand: die Mitteilung wurde in der App geoeffnet.
   *
   * Der Endpoint ist idempotent. Ein Fehler wird nur geloggt — ein fehlgeschlagener Lesestand darf das Lesen
   * nicht stoeren.
   */
  private async markAsRead(newsId: number): Promise<void> {
    try {
      this.readConfirmation.set(await lastValueFrom(this.workNewsV2Service.newsV2ControllerMarkAsRead({ id: newsId })));
    } catch (markAsReadException) {
      LoggerService.ERROR(this, 'markAsReadException', markAsReadException);
    }
  }

  /** Harte Lesebestaetigung: die Person bestaetigt aktiv, die Mitteilung gelesen zu haben. */
  protected async confirmRead(): Promise<void> {
    const newsId = this.news()?.id;
    if (!newsId) {
      return;
    }
    this.confirmLoadingState = 'loading';
    try {
      this.readConfirmation.set(await lastValueFrom(this.workNewsV2Service.newsV2ControllerConfirmRead({ id: newsId })));
      this.confirmLoadingState = 'success';
      this.alertDialogService.showAlert({ title: 'LESEBESTAETIGUNG_ERFASST', feedbackState: 'success' });
    } catch (confirmReadException) {
      this.confirmLoadingState = 'error';
      this.exceptionHandlingService.handleExceptionWithAlert('confirmReadException', confirmReadException);
    }
  }
}
